import { PERMISSIONS } from "@/modules/auth/useSession";
import type {
  DayCloseReportView,
  Ingredient,
  ItemProfitabilityRow,
  MenuItem,
} from "@/lib/api/types";
import { liveOpenBillCount } from "./closedDay";
import { stockStatusOf, type StockStatus } from "./stockStatus";

/**
 * The rules behind the back office's "Today" home (D111, founder 2026-10-02).
 *
 * Pure, so the page spec and the nav spec can both ask the same question —
 * "which blocks does this session get?" — without rendering anything, and so
 * the row gate in `app/manage/rows.ts` can be checked against the blocks it
 * promises.
 *
 * Every figure here is computed from a payload a DESTINATION page already
 * reads — the Z, the menu list, the ingredient list and the item report — so
 * Today adds no server read of its own. That is the answer to the cost worry
 * D87 was rejected on, and it is why nothing in this file asks for a field the
 * payloads do not already carry.
 */

/**
 * How long a Today read stays fresh.
 *
 * One minute: long enough that going from Today to a page and back does not
 * ask the database again, short enough that the figure an owner glances at
 * after a rush is not the one from before it. There is deliberately NO polling
 * interval — Today is read when it is opened, never on a timer.
 */
export const TODAY_STALE_MS = 60_000;

/** How many rows each of the two lists shows — the artboard's five. */
export const TODAY_LIST_LIMIT = 5;

/** Which of Today's blocks a session may read — each one is its endpoint's own gate. */
export type TodayBlocks = {
  /** "Gross sales today" — the Z, `REPORT_OPERATIONS_VIEW` OR `REPORT_VIEW`. */
  sales: boolean;
  /** "Items available" — `GET /restaurant/menu-items`, `MENU_VIEW`. */
  menu: boolean;
  /** "Ingredients to reorder" + "Needs attention" — `COSTING_MANAGE`. */
  stock: boolean;
  /** "Top sellers today" — the item report, `REPORT_VIEW`. */
  topSellers: boolean;
};

/**
 * The blocks this session's permissions open.
 *
 * Each arm is the permission the block's ENDPOINT checks, never a guess from
 * the role: the Z takes either report grant (`RequireAnyPermission` on the
 * controller), the menu list `MENU_VIEW` alone (a `MENU_MANAGE`-only custom
 * role is refused by the API, so it gets no tile rather than a 403), the
 * ingredient list `COSTING_MANAGE`, and the item report `REPORT_VIEW`.
 */
export function todayBlocks(
  permissions: readonly string[] | undefined | null,
): TodayBlocks {
  const held = permissions ?? [];
  const has = (permission: string) => held.includes(permission);
  return {
    sales: has(PERMISSIONS.REPORT_OPERATIONS_VIEW) || has(PERMISSIONS.REPORT_VIEW),
    menu: has(PERMISSIONS.MENU_VIEW),
    stock: has(PERMISSIONS.COSTING_MANAGE),
    topSellers: has(PERMISSIONS.REPORT_VIEW),
  };
}

/** Whether any block renders at all — the page's no-access answer. */
export function hasAnyTodayBlock(blocks: TodayBlocks): boolean {
  return blocks.sales || blocks.menu || blocks.stock || blocks.topSellers;
}

/**
 * "N / M" — of the dishes ON the menu, how many can be ordered right now.
 *
 * The denominator is the items whose catalogue switch is on (`isActive`), not
 * every row the list returns: a paused item is off the menu entirely, and
 * counting it as "not available tonight" would fold the catalogue switch into
 * the 86 switch, which D32 keeps apart. `isAvailable` is the server's answer
 * and already includes a combo whose component was 86'd (`COMPONENT`).
 */
export function menuAvailability(items: readonly MenuItem[]): {
  available: number;
  total: number;
} {
  const onMenu = items.filter((item) => item.isActive);
  return {
    available: onMenu.filter((item) => item.isAvailable).length,
    total: onMenu.length,
  };
}

/**
 * Audit round 2, Phase 3 — «منها N بمكوّن نافد»: of the dishes the tile counts
 * as available, how many contain an ingredient a person marked finished.
 *
 * Off the SAME menu list the tile already reads (`ingredientsMarkedOut`, the
 * field the till's «مكوّن نافد» tag reads — B18), under the same `MENU_VIEW`:
 * no read of its own (D111's cost rule). Only dishes in the tile's numerator:
 * on the menu (`isActive`) and orderable (`isAvailable`) — an ingredient out
 * WARNS, it never un-sells a dish, so these are still sold, and a dish already
 * 86'd is not "available with a warning", it is not available. An API from
 * before B18 sends no list, which counts as none.
 */
export function availableWithIngredientOut(items: readonly MenuItem[]): number {
  return items.filter(
    (item) =>
      item.isActive &&
      item.isAvailable &&
      (item.ingredientsMarkedOut?.length ?? 0) > 0,
  ).length;
}

/**
 * B145 — the bills of the current business day still OPEN, read off the Z the
 * page already holds: its sequence exceptions name every number of the day
 * that is not a billed invoice, and `OPEN` is exactly "a table still eating".
 * No read of its own (D111's cost rule). A closed-but-unpaid cheque is NOT
 * counted here — it is already a sale, and the Z's own method split names it.
 *
 * P6.1: on a day already CLOSED the Z's figures are the saved ones, and this
 * is an operations count, not a figure of the day — so it is the count NOW,
 * which the server sends in the after-close block when it moved since.
 */
export function openBillCount(z: DayCloseReportView): number {
  return liveOpenBillCount(z);
}

/**
 * B145 — drawers still open, off the same Z. An API from before the count
 * existed sends only the flag; then a set flag is an unknown number (null,
 * drawn «—») and a clear one is a known zero.
 */
export function openDrawerCount(z: DayCloseReportView): number | null {
  if (typeof z.openShiftCount === "number") return z.openShiftCount;
  return z.openShiftPending ? null : 0;
}

/** One row of "Needs attention": the ingredient and the stock page's own status. */
export type AttentionRow = {
  ingredient: Ingredient;
  status: Exclude<StockStatus, "ok">;
};

/**
 * The ingredients the STOCK page marks Low or Out — the tile's count.
 *
 * Read through `stockStatusOf`, so "low" stays the server's `isLowStock` and
 * the tile can never disagree with the status pill on the page it links to.
 */
export function ingredientsToReorder(rows: readonly Ingredient[]): AttentionRow[] {
  return rows.flatMap((ingredient) => {
    const status = stockStatusOf(ingredient);
    return status === "ok" ? [] : [{ ingredient, status }];
  });
}

/**
 * How full the shelf is against its minimum — lower is worse.
 *
 * An ingredient with no minimum reaches this list only when it is EMPTY
 * (`stockStatusOf` calls it "out"), and nothing is worse than empty, so it
 * ranks by what is on hand: zero, or below zero when depletion ran ahead of
 * the count.
 */
function fillRatio(ingredient: Ingredient): number {
  const threshold = ingredient.lowStockThreshold;
  if (threshold !== null && threshold > 0) {
    return ingredient.onHandQty / threshold;
  }
  return Math.min(0, ingredient.onHandQty);
}

/**
 * "Needs attention" — the worst `limit` of those, worst first.
 *
 * Worst = the lowest on-hand / minimum ratio. Ties go to an empty shelf before
 * a low one, then by name, so the order is the same on every load.
 */
export function needsAttention(
  rows: readonly Ingredient[],
  limit = TODAY_LIST_LIMIT,
): AttentionRow[] {
  return ingredientsToReorder(rows)
    .sort(
      (a, b) =>
        fillRatio(a.ingredient) - fillRatio(b.ingredient) ||
        (a.status === b.status ? 0 : a.status === "out" ? -1 : 1) ||
        a.ingredient.name.localeCompare(b.ingredient.name),
    )
    .slice(0, limit);
}

/**
 * "Top sellers today" — the dishes that sold the most plates, most first.
 *
 * `unitsSold` is the item report's own count (chargeable, non-voided plates on
 * billed bills that were not written off), so this list and the report agree
 * plate for plate. A dish with no sales is not a top seller. Ties go to the
 * dish that took more food money, then by name, so the order is stable.
 */
export function topSellers(
  rows: readonly ItemProfitabilityRow[],
  limit = TODAY_LIST_LIMIT,
): ItemProfitabilityRow[] {
  return rows
    .filter((row) => row.unitsSold > 0)
    .sort(
      (a, b) =>
        b.unitsSold - a.unitsSold ||
        b.revenueNet - a.revenueNet ||
        a.nameAr.localeCompare(b.nameAr),
    )
    .slice(0, limit);
}
